import { ActivatedRoute } from '@angular/router';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { MatIconRegistry } from '@angular/material/icon';
import { Platform } from '@angular/cdk/platform';
import { SecurityService } from 'src/@dva/services/security.service';
import { SplashScreenService } from 'src/@dva/services/splash-screen.service';
import { ThemeService } from 'src/@dva/services/theme.service';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'dva-root',
  templateUrl: './app.component.html',
})
export class AppComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  constructor(
    private iconRegistry: MatIconRegistry,
    private renderer: Renderer2,
    private themeService: ThemeService,
    @Inject(DOCUMENT) private document: Document,
    private platform: Platform,
    private route: ActivatedRoute,
    public splashScreenService: SplashScreenService,
    private securityService: SecurityService,
  ) {}

  ngOnInit(): void {
    this.initializeApp();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeApp(): void {
    this.loadLayout();
    this.securityService.loadNavigation();
    this.setDefaultLanguage();
    this.handleAuthentication();
    this.updateSelectedCompanyByCcmRedirect();    
  }

  private loadLayout(): void {
    this.setStyle();
    this.setIcons();
    this.setTheme();
    this.setBlink();
  }

  private setStyle(): void {
    this.route.queryParamMap
      .pipe(
        filter((queryParamMap) => queryParamMap.has('style')),
        takeUntil(this.destroy$),
      )
      .subscribe((queryParamMap) => {
        const style = queryParamMap.get('style');
        if (style) this.themeService.setStyle(style);
      });
  }

  private setIcons(): void {
    this.iconRegistry.setDefaultFontSetClass('material-icons-outlined');
  }

  private setTheme(): void {
    this.themeService.theme$.pipe(takeUntil(this.destroy$)).subscribe(([previousTheme, newTheme]) => {
      if (previousTheme) this.renderer.removeClass(this.document.body, previousTheme);
      this.renderer.addClass(this.document.body, newTheme);
    });
  }

  private setBlink(): void {
    if (this.platform.BLINK) {
      this.renderer.addClass(this.document.body, 'is-blink');
    }
  }

  private setDefaultLanguage(): void {
    const language = this.securityService.getLanguageAlias() || 'pt';
    this.securityService.setLanguageDefault(language);
  }

  private handleAuthentication(): void {
    this.checkAndProcessToken('token');
    this.checkAndProcessToken('encodedToken', true);
  }

  private checkAndProcessToken(param: string, validateSession = false): void {
    const token = this.getParamFromUrl(param);
    if (token) {
      this.securityService.setRedirectFromCcm();
      validateSession
        ? this.securityService.validateSessionWithSignin(token)
        : this.securityService.signin({ access_token: token });
    }

  }

  private updateSelectedCompanyByCcmRedirect(): void {
    let selectedCompanyCcmId = this.getParamFromUrl('CcmCompany');
    if (!selectedCompanyCcmId) return;
    let user = this.securityService.getUser();
    let allowedCompany = user.companies.find(c => c.companyId === Number(selectedCompanyCcmId))
    if (allowedCompany) {
      this.securityService.updateUserCompany(allowedCompany);
    }
  }

  private getParamFromUrl(param: string): string | null {
    const url = new URL(window.location.href);
    const value = url.searchParams.get(param);
    return value && value.trim() !== "" ? value : null;
  }
}
